import React, { useEffect, useMemo, useState } from 'react';

import Page from 'components/Page';
import { Button, Form, Layout, Select, Space } from 'antd';
import GlobalFilter from 'components/GlobalFilter/GlobalFilter';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from 'hooks/apollo';
import { fields } from 'shared/constants/results';
import _ from 'lodash';
import ResultsConfig from './ResultsConfig';

import SegmentResult from './SegmentsResult';

const getInitialConfig = () => ({
  metrics: [{
    field: 'visitId',
    aggregation: 'uniqExact',
    name: 'visits',
    label: 'Visits',
  }],
  rowLimit: 100,
});

const resultsConfigsQuery = gql`
  query resultsConfigs {
    resultsConfigs {
      id
      title
      metrics {
        id
        name
        field
        aggregation
        label
      } 
      dimensions {
        id
        name
        field
        label
      }
      filter
      rowLimit
      dimensionLimit
    }
  }
`;

const createResultConfigMutations = gql`
  mutation createResultConfig($input: ResultsConfigInput!) {
    createResultsConfig(input: $input) {
      id
    }
  }
`;

const updateResultConfigMutations = gql`
  mutation updateResultConfig($id: ID!, $input: ResultsConfigInput!) {
    updateResultsConfig(id: $id, input: $input) {
      id
    }
  }
`;

const deleteResultConfigMutations = gql`
  mutation deleteResultConfig($id: ID!) {
    deleteResultsConfig(id: $id) {
      id
    }
  }
`;

function getValuesFromResultsConfig(resultsConfig) {
  return {
    ...resultsConfig,
    metrics: resultsConfig.metrics?.map((metric) => ({ ...metric, table: fields[metric.field].table })),
    dimensions: resultsConfig.dimensions?.map((dimension) => ({ ...dimension, table: fields[dimension.field].table })),
  };
}

function getInputFromValues(values) {
  return {
    ..._.omit(values, 'metrics', 'dimensions', 'id'),
    metrics: values.metrics?.map((metric) => _.omit(metric, 'table', 'id')),
    dimensions: values.dimensions?.map((dimension) => _.omit(dimension, 'table', 'id')),
  };
}

const CustomResult = () => {
  const [globalFilter, setGlobalFilter] = useState();
  const [form] = Form.useForm();
  const [resultsConfig, setResultsConfig] = useState(getInitialConfig());
  const { data, loading, refetch } = useQuery(resultsConfigsQuery);
  const [createResultConfig] = useMutation(createResultConfigMutations);
  const [updateResultConfig] = useMutation(updateResultConfigMutations);
  const [deleteResultConfig] = useMutation(deleteResultConfigMutations);

  useEffect(() => {
    if (resultsConfig) {
      form.setFieldsValue(getValuesFromResultsConfig(resultsConfig));
    }
  }, [resultsConfig]);

  const handleSaveConfig = async () => {
    const values = await form.validateFields();
    const input = getInputFromValues(values);

    if (resultsConfig.id) {
      await updateResultConfig({ variables: { id: resultsConfig.id, input } });
    } else {
      await createResultConfig({ variables: { input } });
    }
    refetch();
  };

  const handleDeleteConfig = async () => {
    await deleteResultConfig({ variables: { id: resultsConfig.id } });
    refetch();
  };

  const resultsConfigs = useMemo(() => data?.resultsConfigs.map((config) => ({ label: config.title, value: config.id })) || [], [data]);

  resultsConfigs.unshift({ label: 'New config', value: 'new' });

  return (
    <Layout>
      <Page>
        <Page.Header
          title="Custom result"
          extra={(
            <Select
              value={resultsConfig.id || 'new'}
              loading={loading}
              options={resultsConfigs}
              onChange={(value) => {
                if (value === 'new') {
                  setResultsConfig(getInitialConfig());
                } else {
                  setResultsConfig(data?.resultsConfigs.find((config) => config.id === value));
                }
              }}
            />
          )}
        />
        <Page.Content title="Configuration">
          <Form form={form} onFinish={handleSaveConfig} layout="vertical">
            <ResultsConfig />
          </Form>
          <Page.Footer>
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  form.submit();
                }}
              >
                Load data
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  handleSaveConfig();
                }}
              >
                Save config
              </Button>
              {resultsConfig.id && (
                <Button
                  danger
                  onClick={() => {
                    handleDeleteConfig();
                  }}
                >
                  Delete config
                </Button>
              )}
            </Space>
          </Page.Footer>
        </Page.Content>

        <Page.Content title="Result">
          <SegmentResult resultConfig={resultsConfig} globalFilter={globalFilter} />
        </Page.Content>

      </Page>
      <GlobalFilter onChange={setGlobalFilter} />
    </Layout>
  );
};

export default CustomResult;
